import { ActionType } from "./enums/ActionType";
import { EmailTrigger } from "./enums/EmailTrigger";

/**
 * `WorkflowExecution.source` — every service that writes an automation-history
 * row. Free-form String on the wire (matches Java, so no Mongo migration), but
 * the cockpit has to know all of them: an unlisted value renders as its own raw
 * key, because `I18nProvider` sets `getMessageFallback = ({key}) => key` and
 * swallows the miss silently in production.
 *
 * ⚠ ONE runtime list, and the type is DERIVED from it. It used to be a bare
 * `type` union here plus a hand-kept string array in `default-returner.ts` —
 * two copies of one fact, and the second one is what the filter picker reads.
 * A `type` union is invisible at runtime, so no test could ever compare them.
 *
 * ⚠ `as const` and the tuple form are load-bearing. `Field.options` is
 * `string[]` and `FIELDS_ACTION_LOG` is annotated `Field[]`, so deriving the
 * union from the field entry widens it to plain `string` — silently, with no
 * tsc error (`noUncheckedIndexedAccess` is off). Hence: declare the tuple HERE,
 * and have `default-returner.ts` import and spread it — never the reverse. The
 * direction also keeps the ~2000-line catalogue module out of every bundle that
 * merely touches this type.
 *
 * Canonical source of truth is the repo that writes the column:
 * `main-consumer/src/lib/workflow/enums.ts` → `LogSource`. Kept honest by
 * `src/types/__tests__/logSource.parity.test.ts`.
 */
export const LOG_SOURCE_OPTIONS = [
  "MAIN",
  "WEBSITE",
  "CHECKIN_APP",
  "ATTENDEE_APP",
  "SCHEDULED_MAINTENANCE",
] as const;

export type LogSourceValue = (typeof LOG_SOURCE_OPTIONS)[number];

/**
 * One field assignment an UPDATE_GUEST action made, as the log recorded it.
 *
 * Written by `main-consumer/src/lib/workflow/executor/update-guest.ts` (per
 * target) and collapsed across a fan-out by the consumer's workflow service.
 * `field` is the wire key exactly as the pipe configured it (`firstName`,
 * `state`, `additionalFields.tier`). `value` is the author's OWN literal
 * operand; a sourced row (main guest / calculation) carries `source` and no
 * value, because that value is another guest's data. Java nulls `value` for a
 * field-restricted caller whose allowlist omits `field`.
 *
 * The three counts are per fan-out target: `changed` targets whose stored
 * value differs after the write, `unchanged` targets that already held it,
 * `skipped` sourced rows that resolved to nothing. A single-target run has
 * exactly one of them at 1.
 */
export interface GuestWrite {
  field: string;
  /** Absent = set. */
  op?: "increment" | "decrement";
  /** Literal rows only; capped at 200 chars by the writer. */
  value?: string;
  /** A literal set with an empty operand — the column was unset. */
  cleared?: true;
  source?: { scope: "mainGuest"; field: string } | { scope: "expr" };
  changed: number;
  unchanged: number;
  skipped: number;
}

/**
 * What became of a SEND_EMAIL action, since `status` alone cannot say: a
 * `skipped` send (suppressed recipient, blocked sender, opt-out, …) and a
 * `deduped` one (another trigger of the same guest write already sent this
 * template to this guest) both stay `SUCCESS` — nothing went wrong, and the
 * consumer's pipe status is `every(SUCCESS)`.
 *
 * Canonical source is the writer, `main-consumer/src/lib/workflow/model.ts`
 * (`mailOutcome`), persisted since W2-5a; the Java `ActionResult` DTO passes
 * both fields through. Kept honest by
 * `src/types/__tests__/workflowExecution.mailOutcome.test.ts`.
 */
export const MAIL_OUTCOMES = ["sent", "skipped", "deduped"] as const;
export type MailOutcome = (typeof MAIL_OUTCOMES)[number];

/**
 * What became of a SEND_SMS action (SMS channel wave). Unlike a mail, an SMS
 * can end `failed` inside a SUCCESS run only in dryrun; a real refusal by the
 * provider stops the pipe as a FAILURE that still carries `smsOutcome:
 * "failed"` and the `smsFailReason`. A multi-recipient action collapses its
 * per-recipient outcomes in the order failed > sent > dryrun > deduped >
 * skipped (consumer `collapseSmsOutcome`).
 *
 * MIRROR: main-consumer `src/lib/workflow/model.ts` ActionResult.smsOutcome
 * (the writer), main-backend `ActionResult` DTO (pass-through). Order is the
 * consumer's declaration order, pinned by
 * `src/types/__tests__/workflowExecution.smsOutcome.test.ts`.
 */
export const SMS_OUTCOMES = ["sent", "failed", "skipped", "deduped", "dryrun"] as const;
export type SmsOutcome = (typeof SMS_OUTCOMES)[number];

/**
 * What became of a NOTIFY_CONTACT ("Mail an Kontakt") action — plan
 * arrival-automation Rev. 4 §1.5. The action is ALWAYS `SUCCESS` (it never
 * fails a pipe); the outcome says whether any contact was mailed, and why
 * not. No reason code, address or label is ever stored beside it — the why of
 * a single skip stays in the consumer's log.
 *  - `sent`: at least one contact got the notice; `none`: contacts were found
 *    but none mailed now (already told about this arrival — one mail per
 *    contact and arrival, Q8 — a storm pause, a suppressed address);
 *  - `disabled`: the kill switch; `privacy-missing`: no valid privacy URL at
 *    send; `no-recipients`: no verified, live contact matched;
 *    `batch-excluded`: a cockpit bulk check-in the action does not include;
 *    `no-guest`: the guest is gone; `unsupported`: not an arrival trigger or a
 *    lane that cannot send; `error`: a config, read or send that failed.
 *
 * MIRROR: main-consumer `src/lib/workflow/model.ts` NOTICE_OUTCOMES (the
 * writer, this order), main-backend `ActionResult` (pass-through). Pinned by
 * `src/types/__tests__/workflowExecution.noticeOutcome.test.ts` against the
 * checked-in copy `notice-outcome-parity.json`.
 */
export const NOTICE_OUTCOMES = [
  "sent",
  "none",
  "disabled",
  "privacy-missing",
  "no-recipients",
  "batch-excluded",
  "no-guest",
  "unsupported",
  "error",
] as const;
export type NoticeOutcome = (typeof NOTICE_OUTCOMES)[number];

export interface ActionResult {
  actionType: ActionType;
  order: number;
  status: "SUCCESS" | "FAILURE";
  errorMessage?: string;
  durationMs: number;
  /**
   * SEND_EMAIL only — see `MAIL_OUTCOMES`. Absent on every other action type
   * and on rows written before W2-5; readers treat absence as "not recorded",
   * never as "sent".
   */
  mailOutcome?: MailOutcome;
  /**
   * Beside `mailOutcome === "skipped"`, and only when the dispatcher named a
   * cause. The vocabulary is the `sent_emails.skipReason` one
   * (`lib/utils/emailSkipReason.ts`) plus future codes — render it as a label
   * and tolerate an unknown one. The consumer's own dispatcher supplies none
   * yet, so a skipped row without a reason is the common case today.
   */
  mailSkipReason?: string;
  /**
   * UPDATE_GUEST only — what the action wrote and whether it changed anything.
   * Absent on every other action type and on rows written before the field
   * existed; readers must treat absence as "not recorded", never as "no
   * writes". See `isNoChangeResult` in `lib/utils/workflowRunOutcome.ts`.
   */
  guestWrites?: GuestWrite[];
  /** SEND_SMS only — see `SMS_OUTCOMES`. Absent ⇒ "not recorded", never "sent". */
  smsOutcome?: SmsOutcome;
  /**
   * Beside `smsOutcome === "skipped"`: the `sent_sms.skipReason` code
   * (`lib/utils/smsSkipReason.ts`). Free string — render through the map and
   * tolerate a code this build does not know.
   */
  smsSkipReason?: string;
  /** Beside `smsOutcome === "failed"` (and dryrun refusals): the `sent_sms.failReason` code (`lib/utils/smsFailReason.ts`). */
  smsFailReason?: string;
  /** SMS parts the body rendered to (billing unit). Absent before the first render. */
  smsParts?: number;
  /** The provider's error class (`__type` + `Reason`) — support detail, never shown as a sentence. */
  smsClass?: string;
  /** NOTIFY_CONTACT only — see `NOTICE_OUTCOMES`. Absent ⇒ "not recorded". */
  noticeOutcome?: NoticeOutcome | null;
  /** NOTIFY_CONTACT only — contacts mailed by this action (an integer ≥ 0); absent / null = not recorded. */
  noticeSent?: number | null;
  /** NOTIFY_CONTACT only — contacts found but not mailed now; absent / null = not recorded. */
  noticeSkipped?: number | null;
  // UXW15 (Phase 15): post-substitution URL + body the HTTP_REQUEST
  // executor sent. Email-redacted server-side via SanitizeHelper and
  // capped at 4 KB. Both fields are null on non-HTTP actions and on
  // HTTP rows older than Phase 15.
  httpRequestUrl?: string;
  httpRequestBody?: string;
}

export interface WorkflowExecution {
  workflowExecutionId: string;
  eventId: string;
  workflowId: string;
  pipeName: string;
  trigger: EmailTrigger;
  guestId: string;
  /**
   * Every guest an action actually touched once target-scope fan-out is
   * resolved (main guest / companions / whole party). The trigger guest is
   * still recorded in `guestId`; this lists ALL affected guests including
   * fanned-out ones. Absent on rows written before the TARGET feature.
   */
  touchedGuestIds?: string[];
  /** Read-only display labels for touchedGuestIds, resolved field-scoped on AI reads. */
  touchedGuestNames?: string[];
  guestFirstName?: string;
  guestLastName?: string;
  guestEmail?: string;
  status: "SUCCESS" | "PARTIAL_FAILURE" | "FAILURE";
  source: LogSourceValue;
  durationMs: number;
  actionResults: ActionResult[];
  errorMessage?: string;
  /**
   * This run was a deliberate re-announcement of an already-made decision
   * (the archive's "run the automations again" opt-in), not a first decision.
   *
   * Written by the consumer since the feature shipped, but neither the Java
   * model nor this type declared it, so the mapper dropped it on read and the
   * stated purpose — telling a re-broadcast apart from an original, which is
   * "otherwise impossible" — was reachable only by querying Mongo by hand.
   * Absent on ordinary runs and on every row written before it existed;
   * `false` would answer a different question than "not recorded".
   */
  retriggered?: boolean;
  createdAt: string;
}
